import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';

/**
 * An open picture book that turns its own pages, for use as card artwork.
 *
 * The spreads are the same illustrations the onboarding adventure picker uses,
 * so the art on the card is real Pixley storybook art rather than stock books.
 */

const SPREADS = [
  '/assets/adventure-pirate.jpg',
  '/assets/adventure-space.jpg',
  '/assets/adventure-dino.jpg',
  '/assets/adventure-ocean.jpg',
];

const FLIP_EVERY_MS = 2800;
const FLIP_DURATION_S = 1.05;
// Let the card finish animating in before the first page turns.
const FIRST_FLIP_DELAY_S = 0.9;

const PAPER = '#FFFDF8';

const prefersReducedMotion = () =>
  typeof window !== 'undefined' &&
  typeof window.matchMedia === 'function' &&
  window.matchMedia('(prefers-reduced-motion: reduce)').matches;

/** One half of the spread: paper with the illustration inset, tight to the gutter. */
const Page = ({ src, side, testId }) => (
  <div
    style={{
      width: '100%',
      height: '100%',
      boxSizing: 'border-box',
      background: PAPER,
      padding: side === 'left' ? '4.5% 2.5% 4.5% 5%' : '4.5% 5% 4.5% 2.5%',
    }}
  >
    <img
      src={src}
      alt=""
      aria-hidden="true"
      draggable="false"
      data-testid={testId}
      style={{
        display: 'block',
        width: '100%',
        height: '100%',
        objectFit: 'cover',
        borderRadius: 2,
      }}
    />
  </div>
);

const StorybookFlipbook = ({ coverColor = '#C23A6A' }) => {
  const [spread, setSpread] = useState(0);
  const [animated] = useState(() => !prefersReducedMotion());

  useEffect(() => {
    if (!animated) return undefined;
    const id = setInterval(() => setSpread((s) => s + 1), FLIP_EVERY_MS);
    return () => clearInterval(id);
  }, [animated]);

  // The turning sheet shows spread+1 on both faces: that image is what sits on
  // the right before the turn and on the left after it, so each remount lands
  // on artwork that already matches the static pages underneath.
  const pageAt = (offset) => SPREADS[(spread + offset) % SPREADS.length];

  return (
    <div
      data-testid="storybook-flipbook"
      className="flex items-center justify-center w-full h-full"
      style={{ perspective: 900 }}
    >
      <div
        style={{
          position: 'relative',
          // Sized so the boards clear the circular frame this sits in rather
          // than getting shaved off by it.
          width: '72%',
          aspectRatio: '1.5 / 1',
          transformStyle: 'preserve-3d',
          transform: 'rotateX(18deg) rotateZ(-4deg)',
        }}
      >
        {/* Hardcover boards, standing proud of the paper on every edge */}
        <div
          style={{
            position: 'absolute',
            inset: '-9% -7%',
            background: coverColor,
            borderRadius: 10,
            boxShadow:
              '0 12px 22px rgba(11,26,51,0.26), inset 0 2px 0 rgba(255,255,255,0.30)',
          }}
        />

        {/* Cut edges of the pages nobody is reading */}
        <div
          style={{
            position: 'absolute',
            inset: '-3.4% -2.4%',
            background: '#E9DFCC',
            borderRadius: 4,
          }}
        />
        <div
          style={{
            position: 'absolute',
            inset: '-1.6% -1.1%',
            background: '#F6EFE1',
            borderRadius: 4,
          }}
        />

        <div
          style={{
            position: 'absolute',
            inset: 0,
            display: 'flex',
            background: PAPER,
            borderRadius: 3,
            overflow: 'hidden',
          }}
        >
          <div style={{ width: '50%', height: '100%' }}>
            <Page src={pageAt(0)} side="left" testId="storybook-page-left" />
          </div>
          <div style={{ width: '50%', height: '100%' }}>
            <Page src={pageAt(2)} side="right" testId="storybook-page-right" />
          </div>
        </div>

        {/* Gutter shadow where the two halves fold into the spine */}
        <div
          style={{
            position: 'absolute',
            top: 0,
            bottom: 0,
            left: '50%',
            width: '14%',
            transform: 'translateX(-50%)',
            background:
              'linear-gradient(90deg, rgba(11,26,51,0) 0%, rgba(11,26,51,0.28) 50%, rgba(11,26,51,0) 100%)',
            pointerEvents: 'none',
          }}
        />

        {animated && (
          <motion.div
            key={spread}
            initial={{ rotateY: 0 }}
            animate={{ rotateY: -180 }}
            transition={{
              duration: FLIP_DURATION_S,
              ease: [0.45, 0.05, 0.25, 1],
              delay: spread === 0 ? FIRST_FLIP_DELAY_S : 0,
            }}
            style={{
              position: 'absolute',
              top: 0,
              left: '50%',
              width: '50%',
              height: '100%',
              transformOrigin: 'left center',
              transformStyle: 'preserve-3d',
            }}
          >
            <div
              style={{
                position: 'absolute',
                inset: 0,
                backfaceVisibility: 'hidden',
                boxShadow: '0 8px 18px rgba(11,26,51,0.30)',
              }}
            >
              <Page src={pageAt(1)} side="right" testId="storybook-turning-front" />
            </div>
            <div
              style={{
                position: 'absolute',
                inset: 0,
                backfaceVisibility: 'hidden',
                transform: 'rotateY(180deg)',
                boxShadow: '0 8px 18px rgba(11,26,51,0.30)',
              }}
            >
              <Page src={pageAt(1)} side="left" testId="storybook-turning-back" />
            </div>
          </motion.div>
        )}
      </div>
    </div>
  );
};

export { SPREADS, FLIP_EVERY_MS };
export default StorybookFlipbook;
